import { Checkbox, List, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { StorefrontOutlined } from '@mui/icons-material';
import { Brand, Brand_Enum } from '@kheops/graphql';
import { useAtomValue } from 'jotai';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRefinementList } from 'react-instantsearch';

import CompanyLogo from '../../common/components/CompanyLogo';
import SearchInput from '../../common/components/SearchInput';
import { brandsAtom } from '../../brands/state';
import useNormalizedCurrentRefinements from '../../hooks/useNormalizedCurrentRefinements';
import FilterDisplay from './FilterDisplay';

export default function BrandsFilter(): React.JSX.Element {
  const { t } = useTranslation(['settings']);
  const [query, setQuery] = useState('');
  const brands = useAtomValue(brandsAtom);
  const { refine } = useRefinementList({ limit: 1000, attribute: 'brand' });
  const currentItems = useNormalizedCurrentRefinements('brand') as string[];

  const filteredBrands = useMemo(() => {
    const loweredCaseQuery = query.toLocaleLowerCase();
    return [...brands]
      .filter((brand) => brand.comment!.toLocaleLowerCase().includes(loweredCaseQuery))
      .sort((a, b) => a.comment!.localeCompare(b.comment!));
  }, [brands, query]);

  const toggleBrand = (brand: Pick<Brand, 'value' | 'comment'>): void => {
    refine(brand.value);
  };

  return (
    <FilterDisplay
      icon={<StorefrontOutlined />}
      label={t('settings:brands')}
      mode="ACCORDION"
      name="brands"
      unmountOnExit={false}
      content={(
        <>
          <SearchInput
            sx={{ width: '100%' }}
            value={query}
            onChange={(inputQuery) => setQuery(inputQuery)}
          />
          <List sx={{ mt: 1.5 }}>
            {filteredBrands?.map((brand) => (
              <ListItemButton
                key={brand.value}
                onClick={() => toggleBrand(brand)}
              >
                <ListItemIcon>
                  <CompanyLogo size="medium" variant="transparent" brand={brand.value as Brand_Enum} sx={{ width: 48 }} />
                </ListItemIcon>
                <ListItemText>
                  {brand.comment}
                </ListItemText>
                <Checkbox checked={currentItems.includes(brand.value)} />
              </ListItemButton>
            ))}
          </List>
        </>
      )}
    />
  );
}
